import React from 'react';
import dayjs from 'dayjs';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { QueryClient, QueryClientProvider } from 'react-query';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/main.scss'
import Layout from '@/components/Layout';
import SegmentScript from '@/components/SegmentScript';
import * as env from '@/env';
import { ErrorBoundary } from '@/error';
import { useRouterChangeAnalytics } from '@/hooks/analytics';
import { useGetUrl } from '@/hooks/getUrl';
import { useHistoryWithTranslations } from '@/hooks/history';
import i18n from '@/i18n';
import { rememberUtmTags } from "@/utilities/utmTagsStore";
import { useLogPageView } from '@separate/hooks/analytics';
import { SingleSignOnProvider } from '@separate/hooks/singleSignOn';
import 'dayjs/locale/es';

if (env.API_MOCKING === 'enabled') {
  require('@/mocks');
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15 * 1000,
      refetchOnWindowFocus: 'always',
      useErrorBoundary: true,
    },
    mutations: {
      useErrorBoundary: true,
    },
  },
});

/* eslint react/prop-types: 0 */
function MyApp({ Component, pageProps }) {
  const { locale, query } = useRouter();
  i18n.changeLanguage(locale);
  dayjs.locale(locale);
  rememberUtmTags(query);
  useHistoryWithTranslations();
  useRouterChangeAnalytics();
  useLogPageView();
  const { canonicalUrl } = useGetUrl();
  const ogLocale = locale === 'en' ? 'en_US' : 'es_ES';

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Head>
          <title>SABEResPODER</title>
          <meta name="description" content="SABEResPODER" key="description" />
          <meta name="google" content="notranslate" />
          <link rel="shortcut icon" href="/favicon.ico?v=2" />
          <link rel="canonical" href={canonicalUrl} />
          <meta content="SABEResPODER" property="og:site_name" />
          <meta content={canonicalUrl}  property="og:url" />
          <meta content={ogLocale} property="og:locale" />
          <SegmentScript />
        </Head>
        <SingleSignOnProvider>
          <Layout Component={Component} pageProps={pageProps} />
        </SingleSignOnProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default MyApp;
